import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../lib/api";
import { AdminButton, AdminInput, AdminTextarea, AdminCard } from "../AdminUI";
import { Save, Loader2, IceCream } from "lucide-react";
import { toast } from "sonner";

const EMPTY = "__NONE__";
const SLOTS = 4;

const TodaysIceTab = () => {
    const [data, setData] = useState(null);
    const [flavors, setFlavors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [slots, setSlots] = useState(Array(SLOTS).fill(EMPTY));

    useEffect(() => {
        Promise.all([
            api.get("/todays-ice"),
            api.get("/flavors"),
        ]).then(([t, f]) => {
            setData(t.data);
            setFlavors(f.data);
            const current = [...(t.data.flavors || [])];
            while (current.length < SLOTS) current.push("");
            setSlots(current.slice(0, SLOTS).map((v) => v || EMPTY));
        });
    }, []);

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-garden-green" /></div>;

    const setSlot = (i, v) => {
        const next = [...slots];
        next[i] = v;
        setSlots(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            const selected = slots.filter((s) => s !== EMPTY);
            const payload = { ...data, flavors: selected };
            const res = await api.put("/todays-ice", payload);
            setData(res.data);
            toast.success("Heutige Sorten gespeichert");
        } catch {
            toast.error("Fehler beim Speichern");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="todays-ice-tab">
            {flavors.length === 0 && (
                <div className="bg-pastel-pink/30 border border-pastel-pink rounded-2xl p-4 text-sm text-deep-green">
                    Sie haben noch keine Sorten angelegt. Legen Sie zuerst im Tab <Link to="#" className="font-semibold underline">"Sorten"</Link> Ihre Eissorten an.
                </div>
            )}

            <AdminCard className="space-y-4">
                <h3 className="font-serif text-xl text-deep-green">Heutiges Softeis</h3>

                <AdminInput
                    label="Überschrift (auf der Webseite)"
                    value={data.headline || ""}
                    onChange={(e) => setData({ ...data, headline: e.target.value })}
                    placeholder="Heutiges Softeis"
                    data-testid="today-headline"
                />

                <div className="grid sm:grid-cols-2 gap-4">
                    <AdminInput
                        label="Datum-Bezeichnung (z.B. 'Heute' oder 'Sa, 12. Juli')"
                        value={data.date_label}
                        onChange={(e) => setData({ ...data, date_label: e.target.value })}
                        data-testid="today-date-label"
                    />
                    <AdminInput
                        label="Heute geöffnet"
                        value={data.todays_hours || ""}
                        onChange={(e) => setData({ ...data, todays_hours: e.target.value })}
                        placeholder="z.B. 13:00 – 19:00 oder Heute geschlossen"
                        data-testid="today-hours"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-deep-green mb-2">Sorten heute (4 Plätze)</label>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {slots.map((slot, i) => (
                            <div key={i} className="relative" data-testid={`today-slot-${i}`}>
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <div className="w-7 h-7 rounded-full bg-leaf-green/30 grid place-items-center">
                                        <IceCream className="w-4 h-4 text-garden-green-dark" />
                                    </div>
                                </div>
                                <select
                                    value={slot}
                                    onChange={(e) => setSlot(i, e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-cream-dark bg-white focus:outline-none focus:ring-2 focus:ring-garden-green/30 text-deep-green text-sm appearance-none cursor-pointer"
                                    data-testid={`today-slot-select-${i}`}
                                >
                                    <option value={EMPTY}>— keine Sorte —</option>
                                    {flavors.map((f) => (
                                        <option key={f.id} value={f.name}>{f.name}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-deep-green/60 mt-2">
                        Plätze mit "— keine Sorte —" werden auf der Webseite nicht angezeigt.
                    </p>
                </div>

                <AdminTextarea
                    label="Notiz (optional)"
                    rows={2}
                    value={data.note}
                    onChange={(e) => setData({ ...data, note: e.target.value })}
                    data-testid="today-note"
                />
            </AdminCard>

            <AdminButton onClick={save} loading={saving} data-testid="today-save">
                <Save className="w-4 h-4" /> Speichern
            </AdminButton>
        </div>
    );
};

export default TodaysIceTab;
